import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { type CategoryType, SLUG_TO_ICON } from "@/components/game-categories";
import {
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";

import useSelectedCategories from "../hooks/use-selected-categories";
import CheckboxFilter from "./checkbox-filter";

interface CategoriesDataType {
  categories: CategoryType[];
}

const QUERY = gql`
  {
    categories {
      id
      name
      slug
      parent {
        id
        name
        slug
      }
    }
  }
`;

interface CategoryLabelProps {
  Icon?: React.FC<React.ComponentProps<"svg">>;
  name: string;
}

function CategoryLabel({ Icon, name }: CategoryLabelProps) {
  return (
    <span className="flex gap-2.5 md:gap-2">
      {Icon && <Icon className="size-4 text-white" />}
      <span>{name}</span>
    </span>
  );
}

interface CategoryFilter {
  divider?: React.ReactNode;
}

export default function CategoryFilter({ divider }: CategoryFilter) {
  const { primaryCategories } = useSelectedCategories();
  const { data } = useQuery<CategoriesDataType>(QUERY, {
    skip: !(
      primaryCategories.includes(CATEGORY_ESCAPE_ROOM) ||
      primaryCategories.includes(CATEGORY_HAUNT)
    ),
  });

  const groupedCategories = useMemo(
    () =>
      (data?.categories || []).reduce(
        (accum, category) => {
          const parentId = category.parent?.slug || "";

          if (!(parentId in accum)) {
            accum[parentId] = [];
          }

          accum[parentId].push(category);

          return accum;
        },
        {} as Record<string, CategoryType[]>
      ),
    [data]
  );

  const options = useMemo(() => {
    const categoriesToOptions = (
      parentSlug: string
    ): [string, React.ReactNode][] =>
      parentSlug in groupedCategories
        ? groupedCategories[parentSlug].map((category) => [
            `${parentSlug}/${category.slug}`,
            <CategoryLabel
              Icon={SLUG_TO_ICON[category.slug]}
              key={category.id}
              name={category.name}
            />,
          ])
        : [];

    if (primaryCategories.includes(CATEGORY_ESCAPE_ROOM)) {
      return categoriesToOptions(CATEGORY_ESCAPE_ROOM);
    }

    if (primaryCategories.includes(CATEGORY_HAUNT)) {
      return categoriesToOptions(CATEGORY_HAUNT);
    }

    return [];
  }, [groupedCategories, primaryCategories]);

  if (!options.length) {
    return null;
  }

  return (
    <div>
      <CheckboxFilter
        label="Categories"
        name="subcategories"
        options={options}
      />
      {divider}
    </div>
  );
}
